<template>
    <OrdersTopBarCard
        ref="TopBar"
        v-model:search="kanban.search"
        :selected-order-uuids="selectedOrderUuids"
        :active-filters-count="kanban.activeFiltersCount"
        :filters="kanban.filters"
        @fetch:orders="fetchOrdersByStatusUuids()"
        @update:filters="(filters, activeFiltersCount, fieldsState) => updateFilters(filters, activeFiltersCount, fieldsState)"
        @mass-actions-state-changed="massActionsIsOn = $event; selectedOrderUuids = []"
    />
    <div
        class="kb-board"
        :class="{ 'kb-board--dragging': boardDrag.isActive }"
        ref="board"
        @mousedown="onBoardMouseDown"
        @scroll="onBoardScroll"
    >
        <div class="kb-col" v-for="status in statuses" :key="status.uuid">
            <div class="kb-col-head justify-content-between">
                <span class="kb-col-title mr-2">{{ status.name }}</span>
                <label class="kb-col-check" :title="$t('labels.select_all_in_status')" v-if="massActionsIsOn">
                    <AppCheckbox :model-value="isAllSelectedInStatus(status.uuid)" @update:model-value="selectAllInStatus(status.uuid)" />
                </label>
            </div>
            <div class="kb-col-rule" :style="{ background: status.color }"></div>
            <div class="kb-col-sub"><span>{{ formatNumber(totalSumByStatusUuids[status.uuid]) }} <span class="text-muted">грн</span></span><span class="kb-count">{{ totalOrdersByStatusUuids[status.uuid] ?? 0 }}</span></div>
            <div class="kb-col-body">
                <component
                    :is="massActionsIsOn ? 'div' : 'VueDraggableNext'"
                    v-bind="massActionsIsOn ? {} : { list: ordersByStatusUuids[status.uuid] || [], group: 'orders' }"
                    @change="(event) => onOrderDrop(event, status.uuid)"
                    :animation="200"
                    class="kb-drag-list"
                >
                    <div v-for="order in ordersByStatusUuids[status.uuid]" :key="order.uuid" :class="['kb-card', { 'kb-card--selectable': massActionsIsOn, 'is-sel': isOrderSelected(order) }]" @click="massActionsIsOn ? selectorOrder(order) : undefined">
                        <div class="kc">
                            <div class="kc-head">
                                <div class="kc-head-l">
                                    <component :is="massActionsIsOn ? 'div' : 'router-link'" :to="massActionsIsOn ? undefined : { name: 'OrderProducts', params: { orderUuid: order.uuid } }" class="kc-id">
                                        <span>{{ order.id }}</span>
                                        <i class="icon-arrow-right13" v-if="!massActionsIsOn"></i>
                                    </component>
                                </div>
                                <span class="kc-date">{{ DateFormatter.formatDate(order.created_at) }}</span>
                            </div>
                            <div class="kc-contact" v-if="order.client || order.source">
                                <div class="kc-ctext" v-if="order.client">
                                    <div class="kc-name">{{ order.client.name }}</div>
                                    <div class="kc-phone">{{ formatPhone(order.client.phone) }}</div>
                                </div>
                                <img v-if="order.source" class="kc-srclogo" :src="order.source.image_file?.url ?? PLACEHOLDER_IMAGE_URL" :alt="order.source.name" :title="order.source.name">
                            </div>
                            <OrderDeliveryCell
                                v-if="order.delivery"
                                :delivery="order.delivery"
                                :order="order"
                                @created="order.delivery = $event"
                            />
                            <div class="kc-rule"></div>
                            <div class="kc-foot">
                                <div class="kc-pay" :style="{ color: order.payments.length && order.payments[0].is_paid ? 'rgb(85, 85, 85)' : 'rgb(154, 154, 154)' }">
                                    <span class="kc-paydot" :style="{ background: order.payments.length && order.payments[0].is_paid ? 'rgb(76, 175, 80)' : 'rgb(199, 199, 199)' }"></span>{{ order.payments.length ? $t(`enums.OrderPaymentTypeEnum.${order.payments[0].type}`) : $t('labels.no_payment') }}<br>
                                </div>
                                <div class="kc-budget">{{ formatNumber(order.sum) }} 
                                    <span>грн</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </component>
                <div :ref="'sentinel-' + status.uuid" :data-status-uuid="status.uuid" class="kb-sentinel"></div>
            </div>
        </div>
    </div>
    <transition name="kb-fade">
        <div
            v-if="boardScroll.hasOverflow"
            class="kb-hscroll"
            ref="scrollbarTrack"
            @mousedown.prevent="onScrollbarTrackClick"
        >
            <div class="kb-hscroll-cols">
                <div
                    v-for="status in statuses"
                    :key="status.uuid"
                    class="kb-hscroll-col"
                ></div>
            </div>
            <div
                class="kb-hscroll-viewport"
                :style="{ width: boardScroll.thumbWidth + 'px', transform: 'translateX(' + boardScroll.thumbLeft + 'px)' }"
                @mousedown.stop.prevent="onThumbMouseDown"
            ></div>
        </div>
    </transition>
</template>
<script>
import OrdersTopBarCard from "./OrdersTopBarCard.vue";
import AppCheckbox from "../../../../../components/new/form/checkbox/AppCheckbox.vue";

import { httpClient } from "../../../../../services/HttpClient.ts";
import qs from "qs";
import DateFormatter from "../../../../../services/DateFormatter.ts";
import { Mask } from "maska";
import { VueDraggableNext } from "vue-draggable-next";
import OrderDeliveryCell from "./OrderDeliveryCell.vue";
import { PLACEHOLDER_IMAGE_URL } from "../../../../constants.ts";

export default {
    name: 'OrdersKanbanPage',
    components: {
        OrdersTopBarCard, VueDraggableNext, AppCheckbox, OrderDeliveryCell
    },
    data() {
        return {
            PLACEHOLDER_IMAGE_URL,
            DateFormatter,
            initializingFromQuery: false,
            fetchOrdersRequestId: 0,
            kanban: {
                searchDebounceTimer: null,
                search: '',
                activeFiltersCount: 0,
                filters: {},
            },
            selectedOrderUuids: [],
            ordersByStatusUuids: {},
            statuses: [],
            totalOrdersByStatusUuids: {},
            totalSumByStatusUuids: {},
            pageByStatusUuids: {},
            loadingByStatusUuids: {},
            scrollObserver: null,
            massActionsIsOn: false,
            boardDrag: {
                isActive: false,
                startX: 0,
                scrollLeft: 0
            },
            boardScroll: {
                hasOverflow: false,
                thumbWidth: 0,
                thumbLeft: 0
            },
            boardScrollDrag: {
                isActive: false,
                startX: 0,
                startScrollLeft: 0
            },
            boardResizeObserver: null
        }
    },
    mounted() {
        this.scrollObserver = new IntersectionObserver(this.handleSentinelIntersection, { threshold: 0 });
        this.boardResizeObserver = new ResizeObserver(() => this.updateScrollbar());
        this.$nextTick(() => {
            if (this.$refs.board) this.boardResizeObserver.observe(this.$refs.board);
        });
        this.initializingFromQuery = true;
        this.initFromQuery();
        this.$nextTick(() => {
            this.initializingFromQuery = false;
        });
    },
    beforeUnmount() {
        if (this.scrollObserver) {
            this.scrollObserver.disconnect();
        }
        document.removeEventListener('mousemove', this.onBoardMouseMove);
        document.removeEventListener('mouseup', this.onBoardMouseUp);
        document.removeEventListener('mousemove', this.onThumbMouseMove);
        document.removeEventListener('mouseup', this.onThumbMouseUp);
        if (this.boardResizeObserver) this.boardResizeObserver.disconnect();
    },
    methods: {
        initFromQuery() {
            const query = qs.parse(window.location.search, { ignoreQueryPrefix: true, arrayLimit: 1000 });
            if (query.search) this.kanban.search = query.search;

            const filters = { ...query };
            delete filters.search;

            if (Object.keys(filters).length > 0) {
                this.$refs.TopBar.restoreFilters(filters);
            }
        },
        updateQuery() {
            const params = {};
            if (this.kanban.search) params.search = this.kanban.search;
            Object.assign(params, this.kanban.filters);

            const queryString = qs.stringify(params);
            this.$router.replace(queryString ? `${this.$route.path}?${queryString}` : this.$route.path);
        },
        async fetchOrdersByStatusUuids() {
            const requestId = ++this.fetchOrdersRequestId;
            this.selectedOrderUuids = [];
            this.pageByStatusUuids = {};
            this.loadingByStatusUuids = {};

            const statusUuids = this.kanban.filters?.status_uuids?.length
                ? this.kanban.filters.status_uuids
                : this.statuses.map(s => s.uuid);

            let requests = [];

            for (let statusUuid of statusUuids) {
                let request = httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders`, {
                    ...this.kanban.filters,
                    status_uuids: [statusUuid],
                    search: this.kanban.search,
                    extra_fields: ["client", "delivery", "source.imageFile", "payments", "delivery.stickerFile", "delivery.waybillFile", "integrationOrder.integration"],
                    limit: 20
                })
                .then(response => {
                    if (requestId !== this.fetchOrdersRequestId) {
                        return;
                    }

                    this.ordersByStatusUuids[statusUuid] = response.data.data;
                    this.totalOrdersByStatusUuids[statusUuid] = response.data.meta.total;
                    this.totalSumByStatusUuids[statusUuid] = response.data.meta.total_sum;
                    this.pageByStatusUuids[statusUuid] = 1;
                });

                requests.push(request);
            }

            await Promise.all(requests);

            if (requestId !== this.fetchOrdersRequestId) {
                return;
            }

            this.$nextTick(() => {
                this.setupSentinelObservers();
                this.updateScrollbar();
            });
        },
        setupSentinelObservers() {
            this.scrollObserver.disconnect();
            for (const status of this.statuses) {
                const currentCount = this.ordersByStatusUuids[status.uuid]?.length ?? 0;
                const total = this.totalOrdersByStatusUuids[status.uuid] ?? 0;
                if (currentCount < total) {
                    const ref = this.$refs['sentinel-' + status.uuid];
                    const sentinel = Array.isArray(ref) ? ref[0] : ref;
                    if (sentinel) {
                        this.scrollObserver.observe(sentinel);
                    }
                }
            }
        },
        handleSentinelIntersection(entries) {
            for (const entry of entries) {
                if (!entry.isIntersecting) continue;
                this.loadMoreOrders(entry.target.dataset.statusUuid);
            }
        },
        async loadMoreOrders(statusUuid) {
            if (this.loadingByStatusUuids[statusUuid]) return;

            const currentCount = this.ordersByStatusUuids[statusUuid]?.length ?? 0;
            const total = this.totalOrdersByStatusUuids[statusUuid] ?? 0;

            if (currentCount >= total) {
                const ref = this.$refs['sentinel-' + statusUuid];
                const sentinel = Array.isArray(ref) ? ref[0] : ref;
                if (sentinel) this.scrollObserver.unobserve(sentinel);
                return;
            }

            this.loadingByStatusUuids[statusUuid] = true;

            const requestId = this.fetchOrdersRequestId;
            const nextPage = (this.pageByStatusUuids[statusUuid] ?? 1) + 1;

            try {
                const response = await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/orders`, {
                    ...this.kanban.filters,
                    status_uuids: [statusUuid],
                    search: this.kanban.search,
                    extra_fields: ["client", "delivery", "source.imageFile", "payments", "integrationOrder.integration"],
                    limit: 20,
                    page: nextPage
                });

                if (requestId !== this.fetchOrdersRequestId) {
                    return;
                }

                const merged = [...(this.ordersByStatusUuids[statusUuid] ?? []), ...response.data.data];
                this.ordersByStatusUuids[statusUuid] = merged;
                this.pageByStatusUuids[statusUuid] = nextPage;

                if (merged.length >= total) {
                    const ref = this.$refs['sentinel-' + statusUuid];
                    const sentinel = Array.isArray(ref) ? ref[0] : ref;
                    if (sentinel) this.scrollObserver.unobserve(sentinel);
                }
            } finally {
                if (requestId === this.fetchOrdersRequestId) {
                    this.loadingByStatusUuids[statusUuid] = false;
                }
            }
        },
        async onOrderDrop(event, statusUuid) {
            if (!event.added) return;

            const orderUuid = event.added.element.uuid;

            await httpClient.patch(`/projects/${this.$route.params.projectUuid}/crm/orders/${orderUuid}`, {
                status_uuid: statusUuid
            });
        },
        async updateFilters(filters, activeFiltersCount = 0) {
            this.kanban.activeFiltersCount = activeFiltersCount;

            let formattedFilters = {};

            for (const [key, value] of Object.entries(filters)) {
                if (typeof value === 'boolean') {
                    formattedFilters[key] = Number(value);
                    continue;
                }

                formattedFilters[key] = value;
            }

            this.kanban.filters = formattedFilters;

            this.updateQuery();
            await this.fetchStatuses();
            await this.fetchOrdersByStatusUuids();
        },
        async fetchStatuses() {
            await httpClient.get(`/projects/${this.$route.params.projectUuid}/crm/statuses`, {
                uuids: this.kanban.filters.status_uuids,
                orders: [{
                    column: 'priority_order', dir: 'asc'
                }],
                limit: 100
            })
            .then(response => {
                this.statuses = response.data.data;
            });
        },
        formatPhone(phone) {
            const mask = new Mask({ mask: '+38 (###) ###-##-##' });
            return mask.masked(phone);
        },
        formatNumber(value) {
            return String(value ?? 0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
        },
        selectorOrder(order) {
            const index = this.selectedOrderUuids.indexOf(order.uuid);
            if (index === -1) {
                this.selectedOrderUuids.push(order.uuid);
            } else {
                this.selectedOrderUuids.splice(index, 1);
            }
        },
        isOrderSelected(order) {
            return this.selectedOrderUuids.includes(order.uuid);
        },
        selectAllInStatus(statusUuid) {
            const orders = this.ordersByStatusUuids[statusUuid] || [];
            const allSelected = orders.every(order => this.selectedOrderUuids.includes(order.uuid));

            if (allSelected) {
                this.selectedOrderUuids = this.selectedOrderUuids.filter(uuid => !orders.some(order => order.uuid === uuid));
            } else {
                for (let order of orders) {
                    if (!this.selectedOrderUuids.includes(order.uuid)) {
                        this.selectedOrderUuids.push(order.uuid);
                    }
                }
            }
        },
        onBoardMouseDown(event) {
            if (event.button !== 0) return;
            if (event.target.closest('.kb-card, .kb-col-head, input, button, a, label')) return;

            const board = this.$refs.board;
            this.boardDrag.isActive = true;
            this.boardDrag.startX = event.pageX - board.offsetLeft;
            this.boardDrag.scrollLeft = board.scrollLeft;

            document.addEventListener('mousemove', this.onBoardMouseMove);
            document.addEventListener('mouseup', this.onBoardMouseUp);
        },
        onBoardMouseMove(event) {
            if (!this.boardDrag.isActive) return;
            event.preventDefault();
            const board = this.$refs.board;
            const x = event.pageX - board.offsetLeft;
            const walk = x - this.boardDrag.startX;
            board.scrollLeft = this.boardDrag.scrollLeft - walk;
        },
        onBoardMouseUp() {
            this.boardDrag.isActive = false;
            document.removeEventListener('mousemove', this.onBoardMouseMove);
            document.removeEventListener('mouseup', this.onBoardMouseUp);
        },
        onBoardScroll() {
            this.updateScrollbar();
        },
        updateScrollbar() {
            const board = this.$refs.board;
            if (!board) return;
            const { scrollLeft, scrollWidth, clientWidth } = board;
            this.boardScroll.hasOverflow = scrollWidth > clientWidth + 1;
            if (!this.boardScroll.hasOverflow) return;
            const track = this.$refs.scrollbarTrack;
            const trackWidth = track ? track.clientWidth : 200;
            this.boardScroll.thumbWidth = Math.max(20, trackWidth * (clientWidth / scrollWidth));
            this.boardScroll.thumbLeft = (scrollLeft / scrollWidth) * trackWidth;
        },
        onScrollbarTrackClick(event) {
            const track = this.$refs.scrollbarTrack;
            if (!track) return;
            const board = this.$refs.board;
            const rect = track.getBoundingClientRect();
            const ratio = (event.clientX - rect.left) / rect.clientWidth;
            board.scrollLeft = ratio * board.scrollWidth - board.clientWidth / 2;
        },
        onThumbMouseDown(event) {
            const board = this.$refs.board;
            this.boardScrollDrag.isActive = true;
            this.boardScrollDrag.startX = event.clientX;
            this.boardScrollDrag.startScrollLeft = board.scrollLeft;
            document.addEventListener('mousemove', this.onThumbMouseMove);
            document.addEventListener('mouseup', this.onThumbMouseUp);
        },
        onThumbMouseMove(event) {
            if (!this.boardScrollDrag.isActive) return;
            event.preventDefault();
            const track = this.$refs.scrollbarTrack;
            const board = this.$refs.board;
            const trackWidth = track ? track.clientWidth : 200;
            const dx = event.clientX - this.boardScrollDrag.startX;
            board.scrollLeft = this.boardScrollDrag.startScrollLeft + (dx / trackWidth) * board.scrollWidth;
        },
        onThumbMouseUp() {
            this.boardScrollDrag.isActive = false;
            document.removeEventListener('mousemove', this.onThumbMouseMove);
            document.removeEventListener('mouseup', this.onThumbMouseUp);
        },
        isAllSelectedInStatus(statusUuid) {
            const orders = this.ordersByStatusUuids[statusUuid] || [];
            return orders.length > 0 && orders.every(order => this.selectedOrderUuids.includes(order.uuid));
        }
    },
    watch: {
        statuses() {
            this.$nextTick(() => this.updateScrollbar());
        },
        'kanban.search'() {
            if (this.initializingFromQuery) return;

            if (this.kanban.searchDebounceTimer) {
                clearTimeout(this.kanban.searchDebounceTimer);
            }
            this.kanban.searchDebounceTimer = setTimeout(() => {
                this.updateQuery();
                this.fetchOrdersByStatusUuids();
                this.kanban.searchDebounceTimer = null;
            }, 500);
        }
    }
}
</script>
<style scoped>
.kb-drag-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 300px;
}
.kb-board {
    display: flex;
    gap: 22px;
    align-items: flex-start;
    overflow-x: auto;
    padding: 5px;
    cursor: grab;
    user-select: none;
    scrollbar-width: none;
}
.kb-board::-webkit-scrollbar {
    display: none;
}
.kb-board--dragging {
    cursor: grabbing;
}
.kb-hscroll {
    position: fixed;
    bottom: 16px;
    right: 20px;
    width: 220px;
    height: 36px;
    background: rgba(240, 240, 240, .95);
    border: 1px solid rgba(0, 0, 0, .1);
    border-radius: 8px;
    z-index: 200;
    cursor: pointer;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, .12);
}
.kb-hscroll-cols {
    display: flex;
    align-items: stretch;
    height: 100%;
    gap: 3px;
    padding: 4px;
    box-sizing: border-box;
}
.kb-hscroll-col {
    flex: 1;
    border-radius: 4px;
    background: #c8c8c8;
}
.kb-hscroll-viewport {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: rgba(255, 255, 255, .35);
    border: 1.5px solid rgba(63, 81, 181, .65);
    border-radius: 7px;
    cursor: grab;
    box-sizing: border-box;
    transition: border-color .15s;
}
.kb-hscroll-viewport:hover {
    border-color: rgba(63, 81, 181, .9);
}
.kb-hscroll-viewport:active {
    cursor: grabbing;
    border-color: #3F51B5;
    background: rgba(63, 81, 181, .1);
}
.kb-fade-enter-active,
.kb-fade-leave-active {
    transition: opacity .2s;
}
.kb-fade-enter-from,
.kb-fade-leave-to {
    opacity: 0;
}
.kb-col {
    flex: 0 0 var(--kb-col-w, 264px);
    width: var(--kb-col-w, 264px);
    background: transparent;
    border: 0;
    border-radius: 12px;
    display: flex;
    flex-direction: column;
    padding: 2px 4px;
    transition: ease-in-out .15s, box-shadow ease-in-out .15s;
}
.kb-col-over,
.kb-col:has(.sortable-ghost) {
    background:rgba(63,81,181,.05);
    box-shadow:0 0 0 1.5px rgba(63,81,181,.22); 
}
.kb-col-head {
    display: flex;
    align-items: center;
    padding: 2px 2px 0;
}
.kb-col-title {
    font-weight: 600;
    font-size: 14px;
    color: #2a2a2a;
    letter-spacing: -.01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.kb-col-rule {
    height: 3px;
    border-radius: 3px;
    margin: 9px 0 0;
    opacity: .9;
}
.kb-col-sub {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 1px 12px;
    font-size: 11px;
    color: #8a8a8a;
    font-weight: 500;
}
.kb-count {
    font-size: 12px;
    line-height: 1;
    color: #9a9a9a;
    font-weight: 500;
    font-variant-numeric: tabular-nums;
}
.kb-col-body {
    padding: 2px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 48px;
}
.kb-card {
    background: #fff;
    border: 1px solid #e6e4e4;
    border-radius: .1875rem;
    box-shadow: 0 1px 2px rgba(16, 24, 40, .05);
    cursor: grab;
    transition: box-shadow ease-in-out .15s, border-color ease-in-out .15s, transform ease-in-out .1s;
}
.kb-card--selectable {
    cursor: pointer;
}
.kb-card.is-sel {
    border-color: #3F51B5;
    box-shadow: 0 0 0 1.5px #3F51B5;
}
.kc {
    padding: 13px 14px;
    display: flex;
    flex-direction: column;
    gap: 11px;
    position: relative;
}
.kc-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}
.kc-head-l {
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;
}
.kc-id {
    font-size: 15px;
    font-weight: 700;
    color: #3F51B5;
    letter-spacing: -.01em;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-variant-numeric: tabular-nums;
    line-height: 1.1;
}
.kc-id:hover {
    color: #303f9f;
}
.kc-id:hover span {
    text-decoration: underline;
}
.kc-id .icon-arrow-right13 {
    font-size: 12px;
    opacity: 0;
    transition: opacity .15s;
}
.kc-id:hover .icon-arrow-right13 {
    opacity: .7;
}
.kc-date {
    font-size: 11px;
    color: #9a9a9a;
    white-space: nowrap;
    flex: 0 0 auto;
}
.kc-contact {
    display: flex;
    align-items: center;
    gap: 10px;
    justify-content: space-between;
}
.kc-ctext {
    min-width: 0;
    flex: 1 1 auto;
}
.kc-name {
    font-size: 13px;
    font-weight: 500;
    color: #222;
    line-height: 1.3;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.kc-phone {
    font-size: 11.5px;
    color: #9a9a9a;
    margin-top: 1px;
    letter-spacing: .01em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.kc-srclogo {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    object-fit: cover;
    flex: 0 0 auto;
    border: 1px solid #eee;
    background: #fff;
}
.kc-rule {
    height: 1px;
    background: #f0eeee;
    margin: 1px 0;
}
.kc-foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
}
.kc-pay {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11.5px;
    color: #555;
}
.kc-budget {
    font-size: 17px;
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: -.01em;
    text-align: right;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}
.kc-paydot {
    width: 8px;
    height: 8px;
    border-radius: 100px;
    flex: 0 0 auto;
}
.kc-budget span {
    font-size: 11px;
    font-weight: 500;
    color: #9a9a9a;
}
.kb-col-check {
    display: inline-flex;
    align-items: center;
    margin-right: 8px;
    flex: 0 0 auto;
}
.kb-col-head label {
    margin-bottom: 0;
}
.kc-check {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
    margin: 0;
    cursor: pointer;
}
</style>